// Angular Files
import {
 Component, EventEmitter, Input, NgZone, Output
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

// Other External Files
import { VGSCardInfo } from 'apps/public-portal/src/app/payment-integrations/base/models';

// Teller Online Files
import { PaymentIntegrationFieldValue } from 'apps/public-portal/src/app/payment-integrations/base/components/payment-integration-form-field/payment-integration-form-field.component';
import { TellerOnlineErrorHandlerService } from 'teller-online-libraries/core';
import { TellerOnlineMessageService, TellerOnlineValidationService } from 'teller-online-libraries/shared';

// Component Files
import { VgsCollectBase } from './vgs-collect-base';

/**
 * Single-field VGS Collect input that captures only a CVV. Used by the
 * lazy re-tokenization flow when a saved credit card needs a fresh CVV
 * to mint a token in a (system, plugin) the saved method wasn't tokenized
 * against (Phase 5). Sibling of vgs-collect-credit / vgs-collect-echeck
 * but stripped to a single VGS iframe.
 */
@Component({
    selector: 'app-vgs-collect-cvv-only',
    templateUrl: './vgs-collect-cvv-only.component.html',
    styleUrls: ['./vgs-collect.component.scss', './vgs-collect-cvv-only.component.scss'],
    host: {
        class: 'vgs-collect payment-method-details payment-method-details--cvv-only'
    },
})
export class VgsCollectCvvOnlyComponent extends VgsCollectBase {
    @Input() public cvvLabel: string = 'CVV';

    /** Emits a fresh CVV token after the user submits successfully. */
    @Output() public cvvCaptured = new EventEmitter<string>();

    constructor(
        ngZone: NgZone,
        errorHandlerService: TellerOnlineErrorHandlerService,
        messageService: TellerOnlineMessageService,
        validationService: TellerOnlineValidationService,
    ) {
        super(ngZone, errorHandlerService, messageService, validationService);

        // VgsCollectBase requires a formGroup + paymentMethodData, but the
        // CVV-only flow lives outside the normal checkout form lifecycle.
        // Provide minimal stand-ins so init runs cleanly.
        this.formGroup = this.formGroup ?? new UntypedFormGroup({});
    }

    protected initFormControls(): void {
        this.formGroup.addControl('cvv', new UntypedFormControl(
            new PaymentIntegrationFieldValue(null, '', 'cvv', this.cvvLabel)
        ));
    }

    protected initVGSFields(): void {
        this._vgsCollectForm.field('#vgsCvvOnly', {
            type: 'card-security-code',
            name: 'cardCvc',
            placeholder: '000',
            validations: ['required', 'validCardSecurityCode'],
        });
    }

    protected mapVGSResponse(data: VGSCardInfo): void {
        this.formGroup.controls.cvv.setValue(new PaymentIntegrationFieldValue(data.cardCvc, ''));
        this.cvvCaptured.emit(data.cardCvc);
    }

    protected getValidationFieldMappings(): { formControl: string, vgsField: string, label: string }[] {
        return [
            { formControl: 'cvv', vgsField: 'cardCvc', label: this.cvvLabel },
        ];
    }

    public generateAdditionalErrors(): { [key: string]: string } {
        const errors: { [key: string]: string } = {};
        if (this.formGroup.controls.cvv?.value?.error) {
            errors.cvv = this.formGroup.controls.cvv.value.error;
        }
        return errors;
    }
}
